import { noop } from 'lodash';
import React, { Component } from 'react';
import styled from 'styled-components';
import { MagnifyingGlass } from '@zocdoc-frontend-common/assets-icons';
import MentalHealthOONModal from '@zocdoc-frontend-common/components-mental-health-insurance-oon-modal';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { CompactModal } from '@zocdoc-mezzanine/components-modal';
import { blue100 } from '@zocdoc-mezzanine/foundations-colors';
import { getSearchUrl } from '../../utils/getSearchUrl';
import {
    OUT_OF_NETWORK_STRINGS,
    IN_NETWORK_BOOKABLE_ONLY_STRINGS,
} from './constants';

/**
 * @param {string} insuranceCarrierId
 * @param {string} insuranceName
 * @param {string} insurancePlanId
 * @param {boolean} isOnlyInNetworkBookable
 * @param {string} procedureId
 * @param {string} professionalName
 * @param {Functions_Shared_Fem_SendMetric} sendMetric
 * @param {boolean} shouldShowMentalHealthOONBenefits
 * @param {string} trackClickCloseModal
 * @param {string} trackClickContinueBookingButton
 * @param {string} trackClickFindDoctorsButton
 * @param {string} whiteLabelVanityUrl
 * @returns {JSX.Element}
 * @constructor
 */
export class OutOfNetworkModalView extends Component {
    static defaultProps = {
        trackClickCloseModal: noop,
        trackClickContinueBookingButton: noop,
        trackClickFindDoctorsButton: noop,
    };

    render() {
        const {
            insuranceCarrierId,
            insuranceCarrierName,
            insuranceName,
            insurancePlanName,
            insurancePlanId,
            shouldShowMentalHealthOONBenefits,
            procedureId,
            professional,
            professionalName,
            whiteLabelVanityUrl,
            sendMetric,
            isOnlyInNetworkBookable,
        } = this.props;

        const searchUrl = getSearchUrl(
            procedureId,
            professional.specialtyId,
            insuranceCarrierId,
            insurancePlanId,
            whiteLabelVanityUrl
        );

        const header = isOnlyInNetworkBookable
            ? professionalName +
              IN_NETWORK_BOOKABLE_ONLY_STRINGS.OutOfNetworkHeader
            : OUT_OF_NETWORK_STRINGS.OutOfNetworkHeader;

        return shouldShowMentalHealthOONBenefits && !isOnlyInNetworkBookable ? (
            <MentalHealthOONModal
                data-test="mh-oon-modal"
                ignoreClickOutside
                onClickCancel={this.handleClickCloseModal}
                onClickContinue={this.handleClickContinueButton}
                beforeClickFindInNetworkDoctors={
                    this.handleClickFindDoctorsButton
                }
                findDoctorsUrl={searchUrl}
                providerName={professional.nameInSentence}
                providerPictureUrl={professional.frontEndCirclePictureUrl}
                insuranceName={`${insuranceCarrierName} ${insurancePlanName}`}
                selfPaySettings={professional.selfPaySettings}
                sendMetric={sendMetric}
            />
        ) : (
            this.renderOutOfNetworkModal(
                header,
                isOnlyInNetworkBookable,
                searchUrl,
                insuranceName,
                professionalName
            )
        );
    }

    handleClickFindDoctorsButton = () => {
        const {
            procedureId,
            trackClickFindDoctorsButton,
            shouldShowMentalHealthOONBenefits,
            isOnlyInNetworkBookable,
        } = this.props;

        trackClickFindDoctorsButton(procedureId, {
            is_mh_test_variant: shouldShowMentalHealthOONBenefits,
            is_only_in_network_bookable: isOnlyInNetworkBookable,
        });
    };

    handleClickCloseModal = () => {
        const {
            insurancePlanId,
            trackClickCloseModal,
            shouldShowMentalHealthOONBenefits,
            isOnlyInNetworkBookable,
        } = this.props;

        trackClickCloseModal({
            insurance_plan_id: insurancePlanId,
            is_mh_test_variant: shouldShowMentalHealthOONBenefits,
            is_only_in_network_bookable: isOnlyInNetworkBookable,
        });
    };

    handleClickContinueButton = () => {
        const {
            trackClickContinueBookingButton,
            shouldShowMentalHealthOONBenefits,
        } = this.props;

        trackClickContinueBookingButton({
            is_mh_test_variant: shouldShowMentalHealthOONBenefits,
        });
    };

    renderOutOfNetworkModal = (
        header,
        isOnlyInNetworkBookable,
        searchUrl,
        insuranceName,
        professionalName
    ) => {
        const primaryButtonProps = isOnlyInNetworkBookable
            ? {
                  beforeClick: this.handleClickFindDoctorsButton,
                  href: searchUrl,
                  children: (
                      <span>
                          <StyledMagnifyingGlass />
                          {IN_NETWORK_BOOKABLE_ONLY_STRINGS.FindDoctorsButton}
                      </span>
                  ),
              }
            : {
                  children: OUT_OF_NETWORK_STRINGS.ContinueBookingButton,
                  onClick: this.handleClickContinueButton,
              };

        const secondaryButtonProps = isOnlyInNetworkBookable
            ? undefined
            : {
                  beforeClick: this.handleClickFindDoctorsButton,
                  href: searchUrl,
                  children: (
                      <span>
                          <StyledMagnifyingGlass />
                          {IN_NETWORK_BOOKABLE_ONLY_STRINGS.FindDoctorsButton}
                      </span>
                  ),
              };

        return (
            <CompactModal
                data-test="out-of-network-find-doctor-modal"
                title={header}
                ignoreClickOutside
                onClose={this.handleClickCloseModal}
                primaryButtonProps={primaryButtonProps}
                secondaryButtonProps={secondaryButtonProps}
            >
                {isOnlyInNetworkBookable ? (
                    <OutOfNetworkMessage data-test="in-network-only-modal-message">
                        {IN_NETWORK_BOOKABLE_ONLY_STRINGS.OutOfNetworkMessage}
                    </OutOfNetworkMessage>
                ) : (
                    <OutOfNetworkMessage data-test="out-of-network-modal-message">
                        {`${professionalName} is out-of-network for the insurance you selected `}
                        <InsuranceInfo>{insuranceName}</InsuranceInfo>. See more
                        doctors that take your carrier and plan with similar
                        availabilities.
                    </OutOfNetworkMessage>
                )}
            </CompactModal>
        );
    };
}

const OutOfNetworkMessage = styled.div`
    color: ${blue100};
    font: 15px/22px ${FontFamily.SharpSansMedium};
`;

const InsuranceInfo = styled.span`
    color: ${blue100};
    font: 15px/22px ${FontFamily.SharpSansBold};
`;

const StyledMagnifyingGlass = styled(MagnifyingGlass)`
    height: 16px;
    padding-right: 6px;
    padding-top: 4px;
    width: 16px;
`;
